import { CardInventoryError } from "@/components/billing/card-inventory-error";
import {
  CardLicenseSummary,
  CardLicenseSummarySkeleton,
} from "@/components/billing/card-license-summary";
import DataTableTeamMembers from "@/components/data-table-team-members/data-table-team-members";
import DialogSendInvitation from "@/components/team/dialog-send-invitation";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToastError } from "@/components/ui/use-toast";
import { useUsers } from "@/contexts/users-provider";
import { useApi } from "@/hooks/useApi";
import { useTeamLicenseInventory } from "@/hooks/use-team-license-inventory";
import { useTranslation } from "@/hooks/useTranslation";
import { TeamInvitationDTO } from "@shaarp/common";
import { PlusCircleIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

export default function TeamUsersPage() {
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();

  const { users, apiFetchLoading, apiFetchUsers } = useUsers();

  const [openDialogInvitation, setOpenDialogInvitation] = useState(false);
  const [invitationPriceId, setInvitationPriceId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [invitations, setInvitations] = useState<TeamInvitationDTO[]>([]);
  const {
    inventory,
    isLoading: inventoryLoading,
    error: inventoryError,
    refetch: fetchInventory,
  } = useTeamLicenseInventory();

  useEffect(() => {
    const init = async () => {
      await apiFetchUsers();
    };
    init();
  }, []);

  const fetchUsersInvitations = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get<TeamInvitationDTO[]>(
        `/my/team/invitations`
      );
      setInvitations(data);
    } catch {
      toastError();
      setInvitations([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsersInvitations();
  }, [fetchUsersInvitations]);

  const onDialogClose = () => {
    setOpenDialogInvitation(false);
    setInvitationPriceId("");
    fetchUsersInvitations();
    apiFetchUsers({ force: true });
    fetchInventory();
  };

  return (
    <>
      {inventoryLoading ? (
        <CardLicenseSummarySkeleton />
      ) : inventory ? (
        <CardLicenseSummary inventory={inventory} users={users} />
      ) : inventoryError ? (
        <CardInventoryError onRetry={fetchInventory} />
      ) : null}

      <Card>
        <CardHeader>
          <div className="flex justify-between">
            <div>
              <CardTitle>{t("team.users.cards.members.title")}</CardTitle>
              <CardDescription>
                {t("team.users.cards.members.description")}
              </CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button
                size="sm"
                onClick={() => {
                  setInvitationPriceId("");
                  setOpenDialogInvitation(true);
                }}
              >
                <PlusCircleIcon className="w-4 h-4 mr-2" />
                {t("team.users.buttons.invite")}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DataTableTeamMembers
            users={users}
            invitations={invitations}
            isLoading={apiFetchLoading || isLoading}
            inventory={inventory}
            onInventoryRefetch={fetchInventory}
          />
        </CardContent>
      </Card>

      {openDialogInvitation && (
        <DialogSendInvitation
          onClose={onDialogClose}
          inventory={inventory}
          defaultPriceId={invitationPriceId}
        />
      )}
    </>
  );
}
